import _ from '@/src/helpers/arrayHelpers';
import { TimeCalculations } from '@/src/helpers/timeCalculations';
import { defineStore } from 'pinia';

export const reportsStore = defineStore('reports', {
  namespaced: true,
  state: () => ({
    chatSessions: [],
    chatSessionsLoading: false,
    inboxes: [],
    inboxesLoading: false,
    appointments: [],
    appointmentsLoading: false,
    groupChatsBy: 'day',
    groupInboxesBy: 'day',
    chatsRange: '',
    inboxRange: '',
    totalSessions: {
      total: 0,
    },
    totalSessionsLoading: false,
    chartTransactions: [],
    loadingTransactions: false,
  }),
  getters: {
    closedSessions() {
      return _.filter(this.chatSessions, (item) => item.status === 'closed');
    },
    canceledSessions() {
      return _.filter(this.chatSessions, (item) => item.status === 'canceled');
    },
    rejectedSessions() {
      return _.filter(this.chatSessions, (item) => item.status === 'rejected');
    },
    missedSessions() {
      return _.filter(this.chatSessions, (item) => item.status === 'timeout');
    },
    todaySessions() {
      return _.filter(this.chatSessions, (item) => TimeCalculations.datesEqual(item.created_at, TimeCalculations.getCurrentTime()));
    },
    inboxMessages() {
      let messages = [];
      this.inboxes.forEach((inbox) => messages.push(...inbox.messages));
      return messages;
    },
    todayMessages() {
      if (!this.inboxMessages) return [];
      return _.filter(this.inboxMessages, (item) => TimeCalculations.datesEqual(item.created_at, TimeCalculations.getCurrentTime()));
    },
    daysSessions() {
      let current = TimeCalculations.rawDateObject(this.chatsRange[0]);
      let diff = TimeCalculations.getDaysFromDate(this.chatsRange[0], this.chatsRange[1]);

      this.groupChatsBy = 'day';
      if (diff >= 32 && diff < 150) {
        this.groupChatsBy = 'week';
      } else if (diff >= 150) {
        this.groupChatsBy = 'month';
      }
      let format = 'll';

      let adding = 'days';
      if (this.groupChatsBy === 'month') format = 'MMM yyyy';
      else if (this.groupChatsBy === 'week') adding = 'week';

      if (this.groupChatsBy === 'month') adding = 'month';

      let filtered = _.chain(this.chatSessions)
        .filter(
          (item) =>
            TimeCalculations.getSecondsFromDate(item.created_at, this.chatsRange[1]) >= 0 &&
            TimeCalculations.getSecondsFromDate(this.chatsRange[0], item.created_at) >= 0
        )
        .groupBy((item) => TimeCalculations.rawFormatBeginningOf(item.created_at, this.groupChatsBy, format))
        .map((group, label) => {
          return {
            label: label,
            data: group,
          };
        })
        .sortBy((item) => TimeCalculations.rawDateObject(item.label, format))
        .value();

      while (TimeCalculations.getSecondsFromDate(current, this.chatsRange[1]) >= 0) {
        if (_.filter(filtered, (item) => item.label === TimeCalculations.rawFormat(current, format)).length === 0) {
          filtered.push({ label: TimeCalculations.rawFormat(current, format), data: [] });
        }

        current = TimeCalculations.rawAddTime(current, 1, adding);
      }
      return _.sortBy(filtered, (item) => TimeCalculations.rawDateObject(item.label, format));
    },
    daysInboxMessages() {
      let current = TimeCalculations.rawDateObject(this.inboxRange[0]);
      let diff = TimeCalculations.getDaysFromDate(this.inboxRange[0], this.inboxRange[1]);

      this.groupInboxesBy = 'day';
      if (diff >= 32 && diff < 150) {
        this.groupInboxesBy = 'week';
      } else if (diff >= 150) {
        this.groupInboxesBy = 'month';
      }

      let format = 'll';
      if (this.groupInboxesBy === 'month') format = 'MMM yyyy';

      let filtered = _.chain(this.inboxMessages)
        .filter(
          (item) =>
            TimeCalculations.getSecondsFromDate(item.created_at, this.inboxRange[1]) >= 0 &&
            TimeCalculations.getSecondsFromDate(this.inboxRange[0], item.created_at) >= 0
        )
        .groupBy((item) => TimeCalculations.rawFormatBeginningOf(item.created_at, this.groupInboxesBy, format))
        .map((group, label) => {
          return {
            label: label,
            data: group,
          };
        })
        .sortBy((item) => TimeCalculations.rawDateObject(item.label, format))
        .value();

      let adding = 'days';
      if (this.groupInboxesBy === 'month') {
        adding = 'month';
      }

      if (this.groupInboxesBy === 'week') adding = 'week';

      while (TimeCalculations.getSecondsFromDate(current, this.inboxRange[1]) >= 0) {
        if (_.filter(filtered, (item) => item.label === TimeCalculations.rawFormat(current, format)).length === 0) {
          filtered.push({ label: TimeCalculations.rawFormat(current, format), data: [] });
        }

        current = current.add(1, adding);
      }

      return _.sortBy(filtered, (item) => TimeCalculations.rawDateObject(item.label, format));
    },
    acceptedAppointments() {
      return _.filter(this.appointments, (item) => item.status === 'accepted');
    },
    rejectedAppointments() {
      return _.filter(this.appointments, (item) => item.status === 'rejected');
    },
    pendingAppointments() {
      return _.filter(this.appointments, (item) => item.status === 'pending');
    },
  },
  actions: {
    setGroupChatsBy(payload) {
      this.groupChatsBy = payload;
    },
    setGroupInboxesBy(payload) {
      this.groupInboxesBy = payload;
    },
    reformatSessions() {
      for (let field in this.totalSessions) {
        let parts = this.totalSessions[field].toString().split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        this.totalSessions[field] = parts.join('.');
      }
    },
    setTotalSessions(value) {
      this.totalSessions = value;
    },
    setLoadingTotalSessions(value) {
      this.totalSessionsLoading = value;
    },
    setChatSessions(value) {
      this.chatSessions = value;
    },
    setChatSessionsLoading(value) {
      this.chatSessionsLoading = value;
    },
    setInboxes(value) {
      this.inboxes = value;
    },
    setInboxesLoading(value) {
      this.inboxesLoading = value;
    },
    setAppointments(value) {
      this.appointments = value;
    },
    setAppointmentsLoading(value) {
      this.appointmentsLoading = value;
    },
    setChatsRangeState(value) {
      this.chatsRange = value;
    },
    setInboxRangeState(value) {
      this.inboxRange = value;
    },
    setChartTransactions(value) {
      this.chartTransactions = value;
    },
    setLoadingTransactions(value) {
      this.loadingTransactions = value;
    },

    //get chart transactions
    getChartTransactions() {
      this.setLoadingTransactions(true);
      return this.$api
        .get('/api/user/transactions/chart')
        .then((res) => {
          this.setChartTransactions(res.data);
          this.setLoadingTransactions(false);
        })
        .catch((error) => {
          console.error(error.response);
          this.setLoadingTransactions(false);
        });
    },
    getChatSessionsStats() {
      this.setChatSessionsLoading(true);
      return this.$api
        .get('/api/user/reports/chat-sessions')
        .then((res) => {
          this.setChatSessions(res.data);
          this.setChatSessionsLoading(false);
          // return res;
        })
        .catch((error) => {
          console.error(error.response);
          this.setChatSessionsLoading(false);
        });
    },
    getInboxesStats() {
      this.setInboxesLoading(true);
      return this.$api
        .get('/api/user/reports/inboxes')
        .then((res) => {
          this.setInboxes(res.data);
          this.setInboxesLoading(false);
        })
        .catch((error) => {
          console.error(error.response);
          this.setInboxesLoading(false);
        });
    },
    getAppointmentsStats() {
      this.setAppointmentsLoading(true);
      return this.$api
        .get('/api/user/reports/appointments')
        .then((res) => {
          this.setAppointments(res.data);
          this.setAppointmentsLoading(false);
        })
        .catch((error) => {
          console.error(error.response);
          this.setAppointmentsLoading(false);
        });
    },
    getTotalSessions() {
      this.setLoadingTotalSessions(true);
      this.$api
        .get('/total-sessions')
        .then((res) => {
          this.setTotalSessions(res.data);
          this.setLoadingTotalSessions(false);
          this.reformatSessions();
        })
        .catch((error) => {
          this.setLoadingTotalSessions(false);
        });
    },
  },
});
